import {
  ArrowDownTrayIcon,
  ChartPieIcon,
  InformationCircleIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid'
import html2canvas from 'html2canvas'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tooltip'

import { TEAMPROFILE } from '../../constants/evaluation-types'
import trackEvents from '../../constants/track-events'
import {
  useLanguageContext,
  useNotificationContext,
  usePartnerContext
} from '../../context'
import { query } from '../../graphql'
import { classNames } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { openPdfLoadingWindow } from '../../helpers/pdf-loading'
import { useChartData } from '../../hooks/use-chart-data'
import Loader from '../activity-indicator'
import Alert from '../alert'
import RadarChart from '../react-chart/radar-chart'
import Button from '../tailwind/Button'
import { sortRadarChartData } from '../talent/space-radar-chart/sortRadarChartData'
import Careers from './careers'
import DimensionOverlay from './dimension-overlay'
import LinearDimensionExample from './examples/linear-dimension-example'
import LinearDimensionExampleWithCareer from './examples/linear-dimension-example-with-career'
import LinearChart from './linear-chart'
import SlideoverConfiguration from './slideover-configuration'
import SlideoverDimensions from './slideover-dimensions'

// Die Dimensionen der CULTURE-Challenge werden in der Team-Analyse vorerst
// nicht angezeigt. Ihre Werte wirken gegenueber dem Selbstbild der Talente
// invertiert, deshalb bleiben sie ausgeblendet, bis die Zuordnung geklaert
// ist. Die beiden alten HEROS_JOURNEY-Skalen bleiben sichtbar.
const HIDDEN_ANSWER_KEYS = ['CULTURE']

const Analyse = ({ spaces, career, id, careers, setSelectedCareer }) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { partner } = usePartnerContext()
  const { success, error } = useNotificationContext()

  const [showDimensions, setShowDimensions] = useState(false)
  const [showConfiguration, setShowConfiguration] = useState(false)
  const [isDownloadingPng, setIsDownloadingPng] = useState(false)
  const [hovered, setHovered] = useState(null)

  const { data, setData, extractChartData, options } = useChartData({
    spaces,
    hidden_answer_keys: HIDDEN_ANSWER_KEYS
  })

  useEffect(() => {
    if (career) setData({ careers: [{ ...career, visible: true }] })
    else setData({ careers: [] })
  }, [career, setData])

  const chartData = extractChartData()
  const radarChartData = sortRadarChartData(chartData)

  // Number of candidate datasets — datasets beyond this index are requirement
  // profiles (careers). Held in a ref so the hover plugin can stay stable.
  const spacesCountRef = useRef(0)
  spacesCountRef.current = radarChartData.spaces.length

  // Currently hovered dimension index, mirrored into a ref so the stable draw
  // plugin can read it without being re-created.
  const hoveredIndexRef = useRef(null)
  hoveredIndexRef.current = hovered ? hovered.index : null

  // Drives the dimension overlay and the axis highlight. Detection is angular:
  // the dimension the cursor points at is the axis whose angle (from the radar
  // center) is closest to the cursor — robust whether hovering the axis label,
  // the grid, or anywhere in between. afterDraw makes that axis glow.
  // Stable identity (deps []) so the chart is not re-created.
  const dimensionHoverPlugin = useMemo(
    () => ({
      id: 'dimensionHover',
      afterEvent: (chart, args) => {
        const { event } = args
        const scale = chart.scales.r
        if (event.type === 'mouseout' || !scale || !scale._pointLabelItems) {
          if (event.type === 'mouseout') setHovered(null)
          return
        }
        if (event.type !== 'mousemove' && event.type !== 'mouseenter') return

        const items = scale._pointLabelItems
        const cursorAngle = Math.atan2(
          event.y - scale.yCenter,
          event.x - scale.xCenter
        )
        let index = 0
        let smallest = Infinity
        for (let i = 0; i < items.length; i++) {
          const cx = (items[i].left + items[i].right) / 2
          const cy = (items[i].top + items[i].bottom) / 2
          const axisAngle = Math.atan2(cy - scale.yCenter, cx - scale.xCenter)
          let diff = Math.abs(cursorAngle - axisAngle)
          if (diff > Math.PI) diff = 2 * Math.PI - diff
          if (diff < smallest) {
            smallest = diff
            index = i
          }
        }

        setHovered((prev) => {
          if (prev && prev.index === index) return prev
          const rows = []
          chart.data.datasets.forEach((dataset, i) => {
            if (!chart.isDatasetVisible(i)) return
            rows.push({
              datasetIndex: i,
              label: dataset.label,
              color: dataset.borderColor,
              value: dataset.data[index],
              isProfile: i >= spacesCountRef.current
            })
          })
          return { index, rows }
        })
      },
      afterDraw: (chart) => {
        const index = hoveredIndexRef.current
        const scale = chart.scales.r
        if (index == null || !scale || !scale._pointLabelItems) return

        const count = scale._pointLabelItems.length
        if (!count) return

        // Glow the sector of the hovered dimension: a wedge between the two
        // neighbouring axes, fading from transparent at the center to a soft
        // indigo at the rim — so the region lights up without hiding the data.
        const tip = scale.getPointPosition(index, scale.drawingArea)
        const axisAngle = Math.atan2(
          tip.y - scale.yCenter,
          tip.x - scale.xCenter
        )
        const half = Math.PI / count

        const { ctx } = chart
        const gradient = ctx.createRadialGradient(
          scale.xCenter,
          scale.yCenter,
          0,
          scale.xCenter,
          scale.yCenter,
          scale.drawingArea
        )
        gradient.addColorStop(0, 'rgba(79, 70, 229, 0)')
        gradient.addColorStop(1, 'rgba(79, 70, 229, 0.22)')

        ctx.save()
        ctx.beginPath()
        ctx.moveTo(scale.xCenter, scale.yCenter)
        ctx.arc(
          scale.xCenter,
          scale.yCenter,
          scale.drawingArea,
          axisAngle - half,
          axisAngle + half
        )
        ctx.closePath()
        ctx.fillStyle = gradient
        ctx.fill()
        ctx.restore()
      }
    }),
    []
  )

  const radarPlugins = useMemo(
    () => [dimensionHoverPlugin],
    [dimensionHoverPlugin]
  )

  const downloadTeamAnalyseAsPng = () => {
    const container = document.getElementById('TEAMANALYSE')

    html2canvas(container).then(function (canvas) {
      const img = canvas.toDataURL('image/png')

      const a = document.createElement('a')
      a.href = img
      a.download = `${id}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setIsDownloadingPng(false)
    })
  }

  const generatePdf = async () => {
    const pdfDocument = openPdfLoadingWindow({ t, language })

    const variables = {
      type: TEAMPROFILE,
      team_analyse_id: id,
      language,
      partner_id: partner.id
    }

    try {
      const pdf = await query({ query: 'generatePdf', variables })
      pdfDocument.location.href = JSON.parse(pdf).headers.Location
      success(t('pdf_success'))
      trackEvent(trackEvents.VIEW_TEAM_PROFILE)
    } catch (err) {
      error()
      console.error(err)
      pdfDocument.close()
    }
  }

  const showSpace = ({ id }) => {
    const item = data.spaces.find((s) => s.id === id)
    item.visible = true

    setData({ spaces: data.spaces })
  }

  const hideSpace = ({ id }) => {
    if (data.spaces.filter((s) => s.visible).length === 1) {
      error(t('team_analyse.slideover_configuration.min_1_space_error'))
      return
    }

    const item = data.spaces.find((s) => s.id === id)
    item.visible = false

    setData({ spaces: data.spaces })
  }

  const activeCategory = data.categories.find(({ active }) => active)

  const dimensionsInActiveCategory = data.dimensions.filter(
    ({ category }) => category === activeCategory.id
  )

  const category_is_invisible = dimensionsInActiveCategory.every(
    ({ visible }) => visible === false
  )

  const dimensionsInChartData = chartData.dimensions.filter(
    ({ category }) => category === activeCategory.id
  )

  const no_data_for_category = dimensionsInChartData.length === 0

  const download_button = {
    CHART: {
      tooltip: t('team_analyse.export_png.title'),
      onClick: () => {
        trackEvent(trackEvents.CLICK_EXPORT_TO_PNG_TEAMANALYSIS)
        downloadTeamAnalyseAsPng()
      }
    },
    LINEAR: {
      tooltip: t('team_analyse.export_pdf.title'),
      onClick: () => {
        trackEvent(trackEvents.CLICK_OPEN_PDF_TEAMANALYSIS)
        generatePdf()
      }
    }
  }[data.mode]

  return (
    <>
      <div className='border-b border-gray-200 pb-5 sm:flex sm:items-end sm:justify-between'>
        <div>
          <h3 className='text-xl font-semibold leading-6 text-gray-900'>
            {t('team_analyse.analyse.title')}
          </h3>
          <p className='mt-2 max-w-4xl text-sm text-gray-500'>
            {t('team_analyse.analyse.description')}
          </p>
        </div>
        <div className='mt-3 flex sm:ml-4 sm:mt-0 sm:gap-x-2'>
          <Button.SecondaryLG
            text={t('team_analyse.analyse.open_dimensions_action')}
            onClick={() => {
              trackEvent(trackEvents.CLICK_MORE_INFORMATION_TEAMANALYSIS)
              setShowDimensions(true)
            }}
          />
          <Button.PrimaryLG
            text={t('team_analyse.analyse.open_settings_action')}
            onClick={() => {
              trackEvent(trackEvents.CLICK_CONFIGURATION_TEAMANALYSIS)
              setShowConfiguration(true)
            }}
          />

          <div>
            <button
              data-tooltip-id={'download-button'}
              data-tooltip-content={download_button.tooltip}
              data-tooltip-place='top'
              onClick={download_button.onClick}
              className='rounded-full bg-blue-600 p-2 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
            >
              <ArrowDownTrayIcon className='h-5 w-5' aria-hidden='true' />
            </button>

            <Tooltip
              id='download-button'
              offset={24}
              className='text-sm font-semibold'
            />
          </div>
        </div>
      </div>

      {/* Incomplete Profiles Alert */}
      {chartData.incompleteProfiles &&
        chartData.incompleteProfiles.length > 0 && (
          <div className='mt-4'>
            <Alert
              type='info'
              title={t('team_analyse.analyse.incomplete_profiles.title')}
              description={
                chartData.incompleteProfiles.length === 1
                  ? t(
                      'team_analyse.analyse.incomplete_profiles.description_single',
                      {
                        name: chartData.incompleteProfiles[0].name,
                        completedCount:
                          chartData.incompleteProfiles[0].completedDimensions,
                        totalCount:
                          chartData.incompleteProfiles[0].totalDimensions
                      }
                    )
                  : t('team_analyse.analyse.incomplete_profiles.description', {
                      names: chartData.incompleteProfiles
                        .map((p) => p.name)
                        .join(', '),
                      hasHave: language === 'de' ? 'haben' : 'have',
                      completedCount: Math.min(
                        ...chartData.incompleteProfiles.map(
                          (p) => p.completedDimensions
                        )
                      ),
                      totalCount:
                        chartData.incompleteProfiles[0].totalDimensions
                    })
              }
            />
          </div>
        )}

      <div className={classNames('py-4')}>
        <span className='mb-2 block text-sm font-medium leading-6 text-gray-900'>
          {t('team_analyse.view_option_title')}
        </span>
        <div className='rounded-md'>
          {[
            {
              Icon: ListBulletIcon,
              active: data.mode === 'LINEAR',
              onClick: () => setData({ mode: 'LINEAR' }),
              text: t('team_analyse.list_view.title')
            },
            {
              Icon: ChartPieIcon,
              active: data.mode === 'CHART',
              onClick: () => setData({ mode: 'CHART' }),
              text: t('team_analyse.radar_chart.title')
            }
          ].map(({ Icon, active, onClick, text }, index) => (
            <button
              key={index}
              onClick={onClick}
              type='button'
              className={classNames(
                'group relative -ml-px inline-flex items-center',
                'px-4 py-3 text-sm font-semibold first:rounded-l-md last:rounded-r-md',
                'ring-1 ring-inset ring-gray-300',
                active ? 'bg-gray-200/70' : 'bg-white hover:bg-gray-50'
              )}
            >
              <Icon
                className={classNames(
                  'h-5 w-5',
                  active
                    ? 'text-gray-700'
                    : 'text-gray-500 group-hover:text-gray-700'
                )}
              />
              <span className='ml-2 font-medium'>{text}</span>
            </button>
          ))}
        </div>
      </div>

      {data.mode === 'CHART' && (
        <>
          <div className='mb-10 mt-3'>
            <Careers
              careers={careers}
              onChange={(id) => {
                // for -1 selected career is undefined
                setSelectedCareer(careers.find((c) => c.id === id))
              }}
            />
          </div>
          <div className='mb-10 xl:flex xl:items-end xl:justify-between'>
            <div>
              <span className='block text-sm font-medium leading-6 text-gray-900'>
                {t('team_analyse.linear_chart.talents.title')}
              </span>
              <span className='mb-3 block text-xs font-normal text-gray-500'>
                <InformationCircleIcon className='mr-1 inline-block h-5 w-5 text-gray-500' />
                {t('team_analyse.linear_chart.talents.display.title')}
              </span>
              <div className='flex flex-wrap gap-x-6 gap-y-2'>
                {[...data.spaces]
                  .sort((a, b) =>
                    a.identification.localeCompare(b.identification)
                  )
                  .map((item, index) => {
                    const {
                      id,
                      borderColor,
                      backgroundColor,
                      identification,
                      visible
                    } = item

                    return (
                      <div
                        key={index}
                        onClick={
                          visible
                            ? () => hideSpace({ id })
                            : () => showSpace({ id })
                        }
                        className='group flex cursor-pointer items-center'
                      >
                        {career ? (
                          <>
                            {visible ? (
                              <EyeIcon className='w-6 text-gray-500 group-hover:text-gray-700' />
                            ) : (
                              <EyeSlashIcon className='w-6 text-gray-500 group-hover:text-gray-700' />
                            )}
                          </>
                        ) : (
                          <div
                            style={{ backgroundColor }}
                            className='h-5 w-5 rounded-full p-1'
                          >
                            <div
                              style={{ backgroundColor: borderColor }}
                              className='h-3 w-3 rounded-full'
                            />
                          </div>
                        )}
                        <span
                          className={classNames(
                            'ml-2 text-sm text-gray-700',
                            !visible && 'line-through'
                          )}
                        >
                          {identification}
                        </span>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
          <div id='TEAMANALYSE' className='relative h-auto w-full'>
            <RadarChart
              data={{
                labels: radarChartData.dimensions.map(
                  ({ property }) => property[language]
                ),
                datasets: [
                  ...radarChartData.spaces.map((space) => ({
                    ...space,
                    data: space.data.map(({ result }) => result)
                  })),
                  ...radarChartData.careers.map((career) => ({
                    ...career,
                    data: career.data.map(({ optimal }) => optimal)
                  }))
                ]
              }}
              options={{
                ...options,
                plugins: {
                  ...options.plugins,
                  legend: {
                    display: false
                  },
                  tooltip: {
                    enabled: false
                  }
                }
              }}
              plugins={radarPlugins}
            />
            <DimensionOverlay
              hovered={hovered}
              dimensionLabel={
                hovered
                  ? radarChartData.dimensions[hovered.index]?.property[language]
                  : ''
              }
            />
          </div>
          <p className='mt-8 text-center text-sm italic text-gray-700'>
            {t('radar_chart.show_hide_hint')}
          </p>
        </>
      )}
      {data.mode === 'LINEAR' && (
        <div>
          <div className='mt-3'>
            <div className='mb-10'>
              <span className='mb-2 block text-sm font-medium leading-6 text-gray-900'>
                {t('team_analyse.linear_chart.category.title')}
              </span>
              <div
                className={classNames(
                  data.mode === 'LINEAR' ? 'flex space-x-4' : 'hidden'
                )}
              >
                {data.categories.map(({ id, active, name, image }, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      const { categories } = data
                      categories.forEach((c) => {
                        if (c.id === id) c.active = true
                        else c.active = false
                      })

                      setData({ categories })
                    }}
                    className={classNames(
                      active
                        ? 'bg-indigo-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700',
                      'flex flex-row items-center rounded-md px-3 py-2 text-sm font-medium'
                    )}
                  >
                    <img src={image} className={classNames('h-5 w-auto')} />
                    <span className={classNames('ml-2.5 block')}>
                      {name[language]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className='my-10'>
              <Careers
                careers={careers}
                onChange={(id) => {
                  // for -1 selected career is undefined
                  setSelectedCareer(careers.find((c) => c.id === id))
                }}
              />
            </div>
            <div className='xl:flex xl:items-end xl:justify-between'>
              <div>
                <span className='block text-sm font-medium leading-6 text-gray-900'>
                  {t('team_analyse.linear_chart.talents.title')}
                </span>
                <span className='mb-3 block text-xs font-normal text-gray-500'>
                  <InformationCircleIcon className='mr-1 inline-block h-5 w-5 text-gray-500' />
                  {t('team_analyse.linear_chart.talents.display.title')}
                </span>
                <div className='flex flex-wrap gap-x-6 gap-y-2'>
                  {[...data.spaces]
                    .sort((a, b) =>
                      a.identification.localeCompare(b.identification)
                    )
                    .map((item, index) => {
                      const {
                        id,
                        borderColor,
                        backgroundColor,
                        identification,
                        visible
                      } = item

                      return (
                        <div
                          key={index}
                          onClick={
                            visible
                              ? () => hideSpace({ id })
                              : () => showSpace({ id })
                          }
                          className='group flex cursor-pointer items-center'
                        >
                          {career ? (
                            <>
                              {visible ? (
                                <EyeIcon className='w-6 text-gray-500 group-hover:text-gray-700' />
                              ) : (
                                <EyeSlashIcon className='w-6 text-gray-500 group-hover:text-gray-700' />
                              )}
                            </>
                          ) : (
                            <div
                              style={{ backgroundColor }}
                              className='h-5 w-5 rounded-full p-1'
                            >
                              <div
                                style={{ backgroundColor: borderColor }}
                                className='h-3 w-3 rounded-full'
                              />
                            </div>
                          )}
                          <span
                            className={classNames(
                              'ml-2 text-sm text-gray-700',
                              !visible && 'line-through'
                            )}
                          >
                            {identification}
                          </span>
                        </div>
                      )
                    })}
                </div>
              </div>
              <div className='mt-12 flex xl:mt-0'>
                <div className='ml-auto' />
                {career ? (
                  <LinearDimensionExampleWithCareer />
                ) : (
                  <>
                    <div className='xl:mb-6' />
                    <LinearDimensionExample />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className='mt-10' />
          <LinearChart
            data={{
              datasets: chartData.spaces,
              // only use one career because of presentation purposes
              career: chartData.careers[0],
              dimensions: chartData.dimensions.filter(
                ({ category }) => category === activeCategory.id
              )
            }}
            setShowConfiguration={setShowConfiguration}
          />
          {category_is_invisible && (
            <div className='mb-12 mt-24 flex justify-center'>
              <button
                onClick={() => setShowConfiguration(true)}
                className='max-w-lg p-2 text-center text-sm italic text-gray-700 hover:text-blue-600'
              >
                {t('team_analyse.linear_chart.category_invisible_hint')}
                <span className='ml-0.5' aria-hidden='true'>
                  &rarr;
                </span>
              </button>
            </div>
          )}
          {no_data_for_category && !category_is_invisible && (
            <div className='mb-12 mt-24 flex justify-center'>
              <span className='max-w-lg text-center text-sm italic text-gray-700'>
                {t('team_analyse.linear_chart.no_data_for_category_hint')}
              </span>
            </div>
          )}
        </div>
      )}
      <SlideoverDimensions open={showDimensions} setOpen={setShowDimensions} />
      <SlideoverConfiguration
        open={showConfiguration}
        setOpen={setShowConfiguration}
        data={data}
        setData={setData}
        showSpace={showSpace}
        hideSpace={hideSpace}
      />
      {isDownloadingPng && (
        <div className='absolute left-0 top-0 flex h-full w-full items-center justify-center bg-slate-900/30'>
          <div className='flex items-center justify-center rounded-lg bg-white p-16'>
            <Loader className={'py-0'} />
          </div>
        </div>
      )}
    </>
  )
}

Analyse.propTypes = {
  spaces: PropTypes.array.isRequired,
  careers: PropTypes.array.isRequired,
  setSelectedCareer: PropTypes.func.isRequired,
  career: PropTypes.object,
  id: PropTypes.string.isRequired
}

Analyse.defaultProps = {
  career: undefined
}

export default Analyse
